import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpRequest, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { FileModel } from './file.model';
import { filter, map } from 'rxjs/operators';

@Injectable({
	providedIn: 'root',
})
export class DocumentManagementService {
	private baseUrl = `${environment.apiUrl}/documentManager`;

	constructor(private http: HttpClient) {}

	index(): Observable<FileModel[]> {
		return this.http.get<FileModel[]>(`${this.baseUrl}/index`);
	}

	/**
	 * Get file URL by ID
	 * @param id File ID
	 * @returns Observable with file URL information
	 */
	getFileUrl(id: number): Observable<any> {
		return this.http.get<any>(`${this.baseUrl}/getFileUrl/id/${id}`);
	}

	/**
	 * Delete file by ID
	 * @param id File ID
	 * @returns Observable with deletion result
	 */
	deleteFile(id: number): Observable<any> {
		return this.http.get<any>(`${this.baseUrl}/deleteFile/id/${id}`);
	}

	/**
	 * Get files by category
	 * @param category File category
	 * @returns Observable with array of files
	 */
	getFilesByCategory(category: number | null): Observable<FileModel[]> {
		if (category) {
			return this.http.get<FileModel[]>(`${this.baseUrl}/getFilesByCategory/category/${category}`);
		} else {
			return this.http.get<FileModel[]>(`${this.baseUrl}/getFilesByCategory`);
		}
	}

	/**
	 * Upload a file with category
	 * @param file File to upload
	 * @param category Category to assign to the file
	 * @param description Optional description
	 * @param metadata Optional metadata
	 * @param filename Optional custom filename
	 * @returns Observable with upload result
	 */
	uploadFile(
		file: File,
		category: string,
		description?: string,
		metadata?: string,
		fileName?: string
	): Observable<FileModel> {
		const formData = new FormData();
		formData.append('file', file);
		formData.append('category', category);

		if (description) {
			formData.append('description', description);
		}

		if (metadata) {
			formData.append('metadata', metadata);
		}

		if (fileName) {
			formData.append('fileName', fileName);
		}

		return this.http.post<FileModel>(`${this.baseUrl}/uploadFile/`, formData);
	}

	/**
	 * Download a file by ID
	 * @param id File ID
	 * @returns Observable with blob data of the file
	 */
	downloadFile(id: number): Observable<Blob> {
		return this.http.get(`${this.baseUrl}/getFileUrl/id/${id}`, {
			responseType: 'blob',
		});
	}

	/**
	 * Update the order position of files
	 * @param orderData Array of objects with file id and new position
	 * @returns Observable with update result
	 */
	updateFileOrder(orderData: { id: number; orderPosition: number }[]): Observable<any> {
		return this.http.post<any>(`${this.baseUrl}/updateFileOrder`, { files: orderData });
	}

	/**
	 * Update the category of multiple files
	 * @param fileIds Array of file IDs to update
	 * @param category New category
	 * @returns Observable with update result
	 */
	bulkUpdateCategory(fileIds: number[], category: string): Observable<any> {
		return this.http.post<any>(`${this.baseUrl}/bulkUpdateCategory`, {
			fileIds: fileIds,
			category: category,
		});
	}

	/**
	 * Delete multiple files
	 * @param fileIds Array of file IDs to delete
	 * @returns Observable with deletion result
	 */
	bulkDeleteFiles(fileIds: number[]): Observable<any> {
		return this.http.post<any>(`${this.baseUrl}/bulkDeleteFiles`, {
			fileIds: fileIds,
		});
	}

	/**
	 * Update a specific field of a file
	 * @param id File ID
	 * @param field Field name to update
	 * @param value New value for the field
	 * @returns Observable with the updated file
	 */
	updateFileField(id: number, field: string, value: string): Observable<any> {
		const data = { [field]: value };
		return this.http.post<any>(`${this.baseUrl}/updateFileField/id/${id}`, data);
	}

	/**
	 * Get all available categories
	 * @returns Observable with array of category objects
	 */
	fetchCategories(): Observable<any> {
		return this.http.get<any>(`${this.baseUrl}/getAllCategories`);
	}

	/**
	 * Add a new category
	 * @param categoryName Name of the new category
	 * @returns Observable with the result
	 */
	addCategory(categoryName: string): Observable<any> {
		return this.http.post<any>(`${this.baseUrl}/addCategory`, { name: categoryName });
	}

	/**
	 * Delete a category
	 * @param categoryId ID of the category to delete
	 * @returns Observable with deletion result
	 */
	deleteCategory(categoryId: number): Observable<any> {
		return this.http.post<any>(`${this.baseUrl}/deleteCategory`, { id: categoryId });
	}

	/**
	 * Download all files in a category
	 * @param categoryId Category ID
	 * @returns Observable with blob data of the files
	 */
	downloadAllFilesByCategory(categoryId: number): Observable<Blob> {
		return this.http
			.get(`${this.baseUrl}/downloadFilesByCategory/category/${categoryId}`, {
				responseType: 'blob',
				headers: new HttpHeaders({
					Accept: 'application/zip, application/octet-stream',
				}),
				observe: 'response',
			})
			.pipe(
				map((response: HttpResponse<Blob>) => {
					return response.body as Blob;
				})
			);
	}
}
